/** Exchange the refresh cookie for a new session. `false` when it could not. */
export type RefreshSession = () => Promise<boolean>

/**
 * One refresh at a time, shared by everyone who asks while it is running.
 *
 * ## Why single-flight is required, not an optimisation
 *
 * The backend **rotates** the pair on every exchange: succeeding invalidates the refresh
 * token that was just spent. So two concurrent refreshes are not merely wasteful — the
 * second presents a token the first has already consumed, gets a 401, and ends the session
 * the first one just renewed.
 *
 * That is not a rare race. An operator opening the dashboard fires six requests at once,
 * and when the access cookie has expired all six come back 401 together. Without this,
 * one of them renews the session and the other five kill it.
 *
 * ## A failed refresh is `false`, never a rejection
 *
 * Callers use this to decide whether to retry, and a decision function that can throw
 * turns every call site into a try/catch. An expired refresh token is the ordinary end of
 * a session, not an exceptional condition.
 */
export function singleFlightRefresh(refresh: RefreshSession): RefreshSession {
  let inFlight: Promise<boolean> | null = null

  return () => {
    // Cleared in `finally` rather than in the caller, so a rejected refresh cannot wedge
    // every later attempt behind a promise that will never be retried.
    inFlight ??= refresh()
      .catch(() => false)
      .finally(() => {
        inFlight = null
      })

    return inFlight
  }
}
